import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import {
  FILES_SECTION_MAX_WIDTH,
  MAIN_SECTION_MIN_WIDTH,
  STACK_GAP,
} from './site-documentation.constants';

const pulseGlow = keyframes`
  0%, 100% {
    opacity: 0.8;
    filter: blur(8px);
  }
  50% {
    opacity: 1;
    filter: blur(10px);
  }
`;

export const AIFormGlow = styled(Stack, {
  name: 'AIFormGlow',
  shouldForwardProp: (prop) => prop !== 'hasAIGlow',
})<{
  hasAIGlow?: boolean;
}>(({ hasAIGlow }) => ({
  position: 'relative',
  zIndex: 0,
  borderRadius: 0,
  overflow: 'visible',
  isolation: 'isolate',

  '&::before': hasAIGlow
    ? {
        mask: 'linear-gradient(to bottom, transparent 0, black -4px, black 100%, transparent 100%)',
        content: '""',
        position: 'absolute',
        inset: 0,
        outline: '10px solid #7417ea',
        clipPath: 'inset(-15px)',
        filter: 'blur(12px)',
        opacity: 1,
        zIndex: 2,
        pointerEvents: 'none',
        background:
          'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.08) 0%, rgba(168,85,247,0.03) 50%, rgba(147,51,234,0.05) 90%)',
        animation: `${pulseGlow} 2.5s ease-in-out infinite`,
      }
    : {},
  '&::after': hasAIGlow
    ? {
        content: '""',
        position: 'absolute',
        inset: 0,
        opacity: 0.9,
        zIndex: 3,
        backdropFilter: 'blur(1px)',
      }
    : {},
}));

export const BottomActionButtons = styled(Stack)(({ theme }) => ({
  position: 'sticky',
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
  padding: '1rem',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  textAlign: 'right',
  boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)',
}));

export const SectionAccordion = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  width: '100%',
  boxShadow: 'none',
  flexShrink: 0,
  '&:before': {
    display: 'none',
  },
}));

export const SectionAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  minHeight: '2.5rem',
  paddingInline: theme.spacing(2),
  '& .MuiAccordionSummary-content': {
    margin: 0,
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
}));

export const SectionAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  paddingLeft: theme.spacing(1),
}));

export const SectionsLayout = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isStackedLayout',
})<{ isStackedLayout: boolean }>(({ isStackedLayout }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  flexWrap: isStackedLayout ? 'wrap' : 'nowrap',
  gap: '10px',
}));

export const MainColumn = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isStackedLayout',
})<{ isStackedLayout: boolean }>(({ isStackedLayout }) => ({
  flexGrow: 1,
  minWidth: isStackedLayout ? 0 : `${MAIN_SECTION_MIN_WIDTH}px`,
  flexBasis: isStackedLayout
    ? '100%'
    : `calc(100% - ${FILES_SECTION_MAX_WIDTH} - ${STACK_GAP}px)`,
}));
